import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Container, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import useAxios from '../../services/AxiosConfig';
import styles from '../ManagementPages.module.css';
import './DeviceManagement.css';
import {  TrashIcon, AddQuestionIcon } from '../../components/icons/Icons';
import { showAlert } from '../../components/PerinHealthAlertSlice';

const AddDevice = () => {

    const groupData = useSelector((state) => state.userInfo.groups);
    const uniqueGroups = useSelector((state) => state.userInfo.uniqueGroups);
    const isAdmin = useSelector((state) => state.userInfo.isAdmin);

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const deviceSlice = useSelector((state)=>state.deviceManagement);
    const allGroupDevices = deviceSlice.allGroupDevices;
    const editDeviceData = deviceSlice.data;
    const updatingDevice = deviceSlice.updating;

    const [errorMessage, setErrorMessage] = useState(null);
    const formRef = useRef(null);
    const [validated, setValidated] = useState(false);

    const [deviceList, setDeviceList] = useState([]);
    const [device, setDevice] = useState({
        id: editDeviceData.deviceId ?? '',
        manufacturer: editDeviceData.manufacturer,
        quantity: editDeviceData.quantity,
    });

    const [deviceEntries, setDeviceEntries] = useState(
        updatingDevice 
            ? editDeviceData.serialNumbers.map(num => ({
                serialNumber: num.serialNumber,
                macAddress: num.macAddress || ''
              }))
            : Array(device.quantity).fill({ serialNumber: '', macAddress: '' })
    );

    const assignedSerialNumbers = Array.from(new Set(editDeviceData.serialNumbers.filter((item)=>item.isAssigned).map((item)=>item.serialNumber)));

    // State to track duplicate entries
    const [duplicateSerialNumbers, setDuplicateSerialNumbers] = useState(new Set());
    const [duplicateMacAddresses, setDuplicateMacAddresses] = useState(new Set());

    const [selectedGroup, setSelectedGroup] = useState(updatingDevice ? editDeviceData.groupId : (uniqueGroups.length ===1 ) ? uniqueGroups[0].id : 0);

    const [selectedOrg, setSelectedOrg] = useState(updatingDevice ? editDeviceData.organizationId : 0);

    const [modelList, setModelList] = useState([]);
    const navigate = useNavigate();
    useEffect(() => {
        const fetchData = async () => {
            httpService.get(`device`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    setDeviceList(transformedDeviceList(responseData.data.data));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });
        };
    
        fetchData();
    }, []);


    const transformedDeviceList = (data) => {
        let uniqueManufacturers = [];
        data.forEach((device)=>{
            if (uniqueManufacturers[device.manufacturer]) {
                uniqueManufacturers[device.manufacturer].models.push({ model: device.model, id: device.id });
            } else {
                uniqueManufacturers[device.manufacturer] = {
                    manufacturer: device.manufacturer,
                    models: [{ model: device.model, id: device.id }]
                };
            }
        });
        return Object.values(uniqueManufacturers);
    };

    const handleGroupChange = (e) => {
        setSelectedGroup(Number(e.target.value));
    };
    
    const handleDeviceChange = (e) => {
        setDevice(prevDevice => ({
            ...prevDevice,
            manufacturer: e.target.value
        }));
        handleModelList(e.target.value);
    };

    const handleModelList = (manufacturer) => {
        let tempModelList = [];
        deviceList.forEach((device) => {
            if(device.manufacturer===manufacturer){
                device.models.forEach((model)=>{
                    tempModelList.push({model:model.model,id:model.id})
                })
            }
        });
        setModelList(tempModelList);
        if (tempModelList.length === 0) {
            setDevice(prevDevice => ({
                ...prevDevice,
                id: ""
            }));
        } else {
            setDevice(prevDevice => ({
                ...prevDevice,
                id: Number(tempModelList[0].id)
            }));
        }
    };

    const handleModelChange = (e) => {
        setDevice(prevDevice => ({
            ...prevDevice,
            id: Number(e.target.value)
        }));
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value;
        if (value === "") {
            setDevice(prevDevice => ({
                ...prevDevice,
                quantity: ""
            }));
            return;
        }
        const numValue = Number(value);
        if (Number.isNaN(numValue)) {
            return;
        }
        setDevice(prevDevice => ({
            ...prevDevice,
            quantity: numValue
        }));
    };

    const handleSerialNumberChange = (index, value) => {
        const updatedEntries = [...deviceEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            serialNumber: value
        };
        setDeviceEntries(updatedEntries);
        
        // Clear duplicate highlighting when user makes changes
        if (duplicateSerialNumbers.has(index)) {
            const newDuplicates = new Set(duplicateSerialNumbers);
            newDuplicates.delete(index);
            setDuplicateSerialNumbers(newDuplicates);
        }
        // Clear error message if it was about duplicates
        if (errorMessage === 'Please ensure all serial numbers and mac addresses are unique.') {
            setErrorMessage(null);
        }
    };

    const handleMacAddressChange = (index, value) => {
        const updatedEntries = [...deviceEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            macAddress: value
        };
        setDeviceEntries(updatedEntries);
        
        // Clear duplicate highlighting when user makes changes
        if (duplicateMacAddresses.has(index)) {
            const newDuplicates = new Set(duplicateMacAddresses);
            newDuplicates.delete(index);
            setDuplicateMacAddresses(newDuplicates);
        }
        // Clear error message if it was about duplicates
        if (errorMessage === 'Please ensure all serial numbers and mac addresses are unique.') {
            setErrorMessage(null);
        }
    };

    const handleSerialDelete = (index) => {
        const updatedEntries = deviceEntries.filter((_, i) => i !== index);
        setDeviceEntries(updatedEntries);
        const newQuantity = device.quantity - 1;
        setDevice(prevDevice => ({
            ...prevDevice,
            quantity: newQuantity
        }));
    };

    const addSerialNumber = () => {
        const updatedEntries = [...deviceEntries, { serialNumber: '', macAddress: '' }];
        setDeviceEntries(updatedEntries);
        const newQuantity = device.quantity + 1;
        setDevice(prevDevice => ({
            ...prevDevice,
            quantity: newQuantity
        }));
    };

    const findDuplicates = (entries) => {
        const serialNumberCounts = {};
        const macAddressCounts = {};
        const duplicateSerials = new Set();
        const duplicateMacs = new Set();

        // Count occurrences
        entries.forEach((entry, index) => {
            if (entry.serialNumber.trim()) {
                serialNumberCounts[entry.serialNumber] = (serialNumberCounts[entry.serialNumber] || []).concat(index);
            }
            if (entry.macAddress.trim()) {
                macAddressCounts[entry.macAddress] = (macAddressCounts[entry.macAddress] || []).concat(index);
            }
        });

        // Find duplicates
        Object.entries(serialNumberCounts).forEach(([serial, indices]) => {
            if (indices.length > 1) {
                indices.forEach(index => duplicateSerials.add(index));
            }
        });

        Object.entries(macAddressCounts).forEach(([mac, indices]) => {
            if (indices.length > 1) {
                indices.forEach(index => duplicateMacs.add(index));
            }
        });

        return { duplicateSerials, duplicateMacs };
    };

    const validateDevice = () => {
        // Clear previous duplicate highlighting
        setDuplicateSerialNumbers(new Set());
        setDuplicateMacAddresses(new Set());

        if (formRef.current) {
            let formIsInvalid = formRef.current.checkValidity() === false;
            setValidated(true); 
            if (formIsInvalid) {
                setErrorMessage('Device Type, Manufacturer, and Quantity are required to proceed');
                return;
            }
        }
        if (device.quantity <= 0) {
            setErrorMessage('Device Type, Manufacturer, and Quantity are required to proceed');
            return;
        }
        let errorMessage = null;
        if (device.id !== "" && device.quantity > 0 && selectedGroup !== 0 && selectedGroup!==""){
            const entriesToCheck = deviceEntries.slice(0, device.quantity);
            const allEntriesFilled = entriesToCheck.every((entry) => 
                entry.serialNumber.trim() !== "" &&  entry.macAddress.trim() !== ""
            );
            if (allEntriesFilled && entriesToCheck.length === device.quantity) {
                let duplicationEntires = entriesToCheck;
                let exisitingDevice = allGroupDevices.find((d) => d.groupId === selectedGroup && d.deviceId === device.id);
                if (exisitingDevice && !updatingDevice) {
                    duplicationEntires = [...entriesToCheck, ...exisitingDevice.serialNumbers];
                }
                const { duplicateSerials, duplicateMacs } = findDuplicates(duplicationEntires);
                
                if (duplicateSerials.size === 0 && duplicateMacs.size === 0) {
                    if (updatingDevice) {
                        updateDevice()
                    } else {
                        sendDevice();
                    }
                } else {
                    // Set duplicate highlighting
                    setDuplicateSerialNumbers(duplicateSerials);
                    setDuplicateMacAddresses(duplicateMacs);
                    errorMessage = exisitingDevice ? 'Serial numbers and mac addresses are duplicated with existing device.' : 'Please ensure all serial numbers and mac addresses are unique.'
                }
            } else {
                errorMessage = 'Please fill in all serial numbers and mac addresses.';
            }
        }
        setErrorMessage(errorMessage);
    };

    const sendDevice = async () => {
        const entriesToSend = deviceEntries.slice(0, device.quantity);
        const deviceInfo = {
            "groupId": selectedGroup,
            "device": device,
            "serialNumbers": entriesToSend
        };
        try {
            const response = await httpService.request({
                method: 'POST',
                headers: {"Content-Type": "application/json"},
                url: 'device/group',
                data: JSON.stringify(deviceInfo),
            });
            if (response) {
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Added device successfully`,
                        type: 'success'
                    })
                );
                navigate('/device-management')
            }
        } catch (error) {
            console.log("error:"+error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Could not add device",
                    type: 'danger',
                })
            );
        }
    };

    const updateDevice = async () => {
        if (device.quantity === "" || device.quantity <= 0) {
            alert("Please enter a valid quantity");
        } else {
            const entriesToSend = deviceEntries.slice(0, device.quantity);
            const info = { 
                quantity: Number(device.quantity),
                serialNumbers: entriesToSend
            };
            try {
                const response = await httpService.request({
                    method: 'PUT',
                    headers: { "Content-Type": "application/json" },
                    url: `device/group/${editDeviceData.id}`,
                    data: JSON.stringify(info),
                });
                if (response) {
                    dispatch(
                        showAlert({
                            header: "Success",
                            message: "Device updated successfully",
                            type: 'success'
                        })
                    );
                    navigate('/device-management');
                }
            } catch (error) {
                console.log("error:" + error);
                dispatch(
                    showAlert({
                        header: "Error",
                        message: "Error updating device",
                        type: 'danger',
                        dismissable: true
                    })
                );
            }
        }
    };

    return (
        <div className={styles.MainCard}>
            <div className={styles.ContentTitle}>{updatingDevice ? "Update Device" : "Add Device"}</div>
            <div className={styles.HideScrollContainer}>
                <Form ref={formRef} noValidate validated={validated}>
                {isAdmin &&
                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingSelect" label="Organization">
                        <Form.Select
                            disabled={updatingDevice}
                            value={selectedOrg}
                            style={{width:"300px"}}
                            onChange={(e)=>setSelectedOrg(Number(e.target.value))}
                            className={`${styles.BorderedComponent}`}
                            required
                        >
                            <option value={''}>Select an Organization</option>
                            {groupData.map((org)=> (
                                <option key={org.organizationId} id={org.organizationId} value={org.organizationId}>{org.organizationName}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            Please select an organization.
                        </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>
                }
                <Form.Group className="mb-3">
                    <FloatingLabel controlId="deviceGroup" label="Group">
                    <Form.Select
                        className={`${styles.BorderedComponent}`}
                        onChange={handleGroupChange}
                        value={selectedGroup}
                        required
                        style={{width: "300px"}}
                    >
                        <option value="">Select a group</option>
                        {isAdmin ? 
                            groupData.filter(organization => organization.organizationId === selectedOrg)
                                .map((organization) => (
                                    organization.groups.map((group) => (
                                        <option key={group.groupId} value={group.groupId}>
                                        {group.groupName}
                                        </option>
                                    ))
                                ))
                            :
                            uniqueGroups.map((group)=> (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))
                        }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        Please select a group.
                    </Form.Control.Feedback>
                    </FloatingLabel>
                </Form.Group>
                <Container style={{paddingLeft:"0px", marginLeft:"0"}}>
                    
                        <Row className='device-row' gap={10} xs="auto">
                            <Col>
                                <Form.Group className="mb-3">
                                <FloatingLabel controlId="deviceModel" label="Device">
                                <Form.Select
                                    className={`${styles.BorderedComponent}`}
                                    onChange={handleDeviceChange}
                                    style={{width:"300px"}}
                                    disabled={updatingDevice}
                                    value={device.manufacturer}
                                    required
                                >
                                    <option value="">Select a device</option>
                                    {deviceList.map((item, index) => (
                                        <option key={index} value={item.manufacturer}>{item.manufacturer}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    Please select a device.
                                </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <FloatingLabel controlId="deviceManufacturer" label="Manufacturer/Model">
                                    <Form.Select
                                        className={`${styles.BorderedComponent}`}
                                        onChange={handleModelChange}
                                        disabled={updatingDevice}
                                        style={{width:"300px"}}
                                        value={device.id}
                                        required
                                    >
                                    {updatingDevice && <option value={device.id}>{editDeviceData.model}</option>}
                                    <option value="">Select a manufacturer/model</option>
                                    {modelList.map((item, index) => (
                                        <option key={index} value={item.id}>{item.model}</option>
                                    ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        Please select a manufacturer/model.
                                    </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <FloatingLabel 
                                        controlId="deviceQuantity"
                                        label="Quantity*"
                                        className="mb-3"
                                    >
                                    <Form.Control
                                        placeholder="Quantity*"
                                        className={`${styles.BorderedComponent}`}
                                        style={errorMessage && device.quantity <= 0 ? {width:"130px", border: "1px solid #DC3545"} : {width:"130px"}}
                                        value={device.quantity}
                                        onChange={handleQuantityChange}
                                        maxLength={3}
                                        disabled={updatingDevice}
                                        isInvalid={errorMessage && device.quantity <= 0}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please select a valid quantity.
                                    </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>
                            </Col>
                        </Row>
                    <div>
                    {Array.from({ length: device.quantity }).map((_, index) => (
                        <div key={`device_entry_${index}`} style={{padding: "10px 0", justifyContent: "flex-start", display:"flex", alignItems:"center", gap:"15px"}}>
                            <Form.Group key={`serial_${index}`} >
                                <FloatingLabel controlId={`serialNumber${index}`} label={`Serial Number ${index + 1}`}>
                                    <Form.Control
                                        type="text"
                                        placeholder={`Serial Number ${index + 1}`}
                                        value={deviceEntries[index]?.serialNumber || ''}
                                        onChange={(e) => handleSerialNumberChange(index, e.target.value)}
                                        required
                                        isInvalid={duplicateSerialNumbers.has(index)}
                                        style={duplicateSerialNumbers.has(index) ? {border: "2px solid #DC3545"} : {}}
                                    />
                                    {duplicateSerialNumbers.has(index) && (
                                        <Form.Control.Feedback type="invalid">
                                            This serial number is duplicated.
                                        </Form.Control.Feedback>
                                    )}
                                </FloatingLabel>
                            </Form.Group>
  
                            <Form.Group key={`mac_${index}`} >
                                <FloatingLabel controlId={`macAddress${index}`} label={`Mac Address`}>
                                    <Form.Control
                                        type="text"
                                        required
                                        placeholder={`Mac Address`}
                                        value={deviceEntries[index]?.macAddress || ''}
                                        onChange={(e) => handleMacAddressChange(index, e.target.value)}
                                        isInvalid={duplicateMacAddresses.has(index)}
                                        style={duplicateMacAddresses.has(index) ? {border: "2px solid #DC3545"} : {}}
                                    />
                                    {duplicateMacAddresses.has(index) && (
                                        <Form.Control.Feedback type="invalid">
                                            This MAC address is duplicated.
                                        </Form.Control.Feedback>
                                    )}
                                </FloatingLabel>
                            </Form.Group>
     
                        {!assignedSerialNumbers.includes(deviceEntries[index]?.serialNumber) ?
                            <div onClick={()=>handleSerialDelete(index)} >
                                <TrashIcon />
                            </div>
                            :
                            <div style={{color: "red", fontSize:"13px"}}>
                                {"Active"}
                            </div>
                        }
                 
                    </div>
                    ))}
                </div>
                <Button variant="primary" style={{marginBottom:"30px"}} className={styles.AddAnotherButton} onClick={addSerialNumber}>
                    <AddQuestionIcon /> Add Serial Number
                </Button>
                </Container>
                </Form>
            </div>
            <div className={styles.ButtonsFooterContainerWithError}>
                {errorMessage &&
                    <div className="page-error">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                        {errorMessage}
                    </div>
                }
                <div style={{ display: "flex", columnGap: "10px" }}>
                    <Button variant="primary" className={styles.CreateButton}
                        onClick={validateDevice}>
                        {updatingDevice ? "Update" : "Add"}
                    </Button>
                    <Button variant="secondary" className={styles.CancelButton} onClick={() => navigate('/device-management')}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );

}

export default AddDevice;